import { computed, ref } from 'vue'
import type { FeatureSpec, FeatureSpecItem } from '@/constants/manageFeatureSpecs'

export interface FeatureTourStep {
  name: string
  description: string
  target?: string
}

const active = ref(false)
const stepIndex = ref(0)
const steps = ref<FeatureTourStep[]>([])
const pageTitle = ref('')
const hole = ref({ top: 0, left: 0, width: 0, height: 0, ready: false })

let resizeObserver: ResizeObserver | null = null
let observedEl: Element | null = null

function clearObserver() {
  if (resizeObserver && observedEl) {
    resizeObserver.unobserve(observedEl)
  }
  observedEl = null
}

let measureRetryTimer: ReturnType<typeof setTimeout> | null = null

function clearMeasureRetry() {
  if (measureRetryTimer) {
    clearTimeout(measureRetryTimer)
    measureRetryTimer = null
  }
}

function bindHole(el: HTMLElement) {
  el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })

  const update = () => {
    const rect = el.getBoundingClientRect()
    const pad = 8
    hole.value = {
      top: Math.max(8, rect.top - pad),
      left: Math.max(8, rect.left - pad),
      width: Math.min(window.innerWidth - 16, rect.width + pad * 2),
      height: Math.min(window.innerHeight - 16, rect.height + pad * 2),
      ready: true,
    }
  }

  window.setTimeout(update, 220)
  update()

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(el)
    observedEl = el
  }
}

/** 모달 등 늦게 나타나는 타깃을 잠시 재시도한다. */
function measureTarget(target?: string, attempt = 0) {
  clearObserver()
  clearMeasureRetry()
  hole.value = { top: 0, left: 0, width: 0, height: 0, ready: false }
  if (!target || typeof document === 'undefined') return

  const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
  if (!el) {
    if (attempt < 8) {
      measureRetryTimer = setTimeout(() => measureTarget(target, attempt + 1), 120)
    }
    return
  }

  bindHole(el)
}

/** 지금 DOM에 보이고 하이라이트할 수 있는 타깃만 투어에 넣는다. */
function isTourTargetReady(target?: string): boolean {
  if (!target || typeof document === 'undefined') return false
  const el = document.querySelector(`[data-tour="${target}"]`) as HTMLElement | null
  if (!el) return false
  const style = getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

function buildSteps(spec: FeatureSpec): FeatureTourStep[] {
  const intro: FeatureTourStep = {
    name: spec.title,
    description: `${spec.summary}\n\n${spec.purpose}`,
    target: 'page-title',
  }
  const featureSteps = spec.features
    .filter(
      (item: FeatureSpecItem) =>
        Boolean(item.target) &&
        item.target !== 'manage-feature-search' &&
        isTourTargetReady(item.target),
    )
    .map((item: FeatureSpecItem) => ({
      name: item.name,
      description: item.description,
      target: item.target,
    }))
  return [intro, ...featureSteps]
}

function syncHole() {
  const step = steps.value[stepIndex.value]
  measureTarget(step?.target)
}

export function useFeatureTour() {
  const current = computed(() => steps.value[stepIndex.value] || null)
  const total = computed(() => steps.value.length)
  const isLast = computed(() => stepIndex.value >= steps.value.length - 1)
  const isFirst = computed(() => stepIndex.value <= 0)

  function startTour(
    spec: FeatureSpec,
    options: { featureName?: string; target?: string } = {},
  ) {
    /* 단일 기능 하이라이트: 타깃이 지금 없으면 투어를 시작하지 않는다 */
    if (options.target && !isTourTargetReady(options.target)) {
      return
    }
    if (options.featureName) {
      const item = spec.features.find((feature) => feature.name === options.featureName)
      if (item?.target && !isTourTargetReady(item.target)) {
        return
      }
    }

    steps.value = buildSteps(spec)
    if (!steps.value.length) return

    pageTitle.value = spec.title
    let index = 0
    if (options.featureName) {
      const byName = steps.value.findIndex((step) => step.name === options.featureName)
      if (byName >= 0) index = byName
    } else if (options.target) {
      const byTarget = steps.value.findIndex((step) => step.target === options.target)
      if (byTarget >= 0) index = byTarget
    }
    stepIndex.value = index
    active.value = true
    document.body.classList.add('is-feature-tour')
    syncHole()
  }

  function stopTour() {
    active.value = false
    steps.value = []
    stepIndex.value = 0
    clearObserver()
    clearMeasureRetry()
    hole.value = { top: 0, left: 0, width: 0, height: 0, ready: false }
    document.body.classList.remove('is-feature-tour')
  }

  function nextStep() {
    if (isLast.value) {
      stopTour()
      return
    }
    stepIndex.value += 1
    syncHole()
  }

  function prevStep() {
    if (isFirst.value) return
    stepIndex.value -= 1
    syncHole()
  }

  function refreshHole() {
    if (active.value) syncHole()
  }

  return {
    active,
    stepIndex,
    steps,
    pageTitle,
    hole,
    current,
    total,
    isLast,
    isFirst,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    refreshHole,
  }
}
